import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api.js'

/**
 * The business's own pictures, in sets.
 *
 * A shop gets its material by having its website read. A creator has no website — what they have
 * is a phone full of photographs and something to say about them. This is where that goes: a
 * sentence, then the pictures, and everything after it is the platform that already exists.
 *
 * Deliberately plain. It is a place to put things, not a gallery: five thumbnails, an add button,
 * and a way to take one back out.
 */
export default function ContentSets({ onPostsWritten }) {
  const { t } = useTranslation()

  const [sets, setSets] = useState(null)
  const [about, setAbout] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const load = async () => {
    try {
      setSets(await api.contentSets())
    } catch (failure) {
      setError(failure.message)
    }
  }

  useEffect(() => {
    load()
    // Once, on the way in. Everything after this reloads because something was just done here.
  }, [])

  /** Runs one action and reloads, saying what went wrong instead of failing silently. */
  const act = async (action) => {
    setBusy(true)
    setError(null)

    try {
      await action()
      await load()
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  const create = () => {
    const said = about.trim()

    if (said.length === 0) return

    act(async () => {
      await api.createContentSet(said)
      setAbout('')
    })
  }

  return (
    <section className="card">
      <h2>{t('sets.title')}</h2>
      <p className="muted">{t('sets.help')}</p>

      <div className="sets__new">
        <label className="field">
          {t('sets.about')}
          <input
            type="text"
            value={about}
            maxLength={300}
            placeholder={t('sets.aboutHint')}
            disabled={busy}
            onChange={(event) => setAbout(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') create()
            }}
          />
        </label>

        <button type="button" className="button" disabled={busy || about.trim().length === 0} onClick={create}>
          {t('sets.create')}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {sets?.length === 0 && <p className="muted">{t('sets.none')}</p>}

      {sets?.map((set) => (
        <div key={set.id} className="set">
          <div className="set__head">
            <strong>{set.about}</strong>
            <span className="muted small">{t('sets.count', { count: set.pictures.length })}</span>
          </div>

          <div className="set__pictures">
            {set.pictures.map(({ publicUrl }) => (
              <div key={publicUrl} className="set__picture">
                <img src={publicUrl} alt="" loading="lazy" />
                <button
                  type="button"
                  className="set__remove"
                  disabled={busy}
                  aria-label={t('sets.removePicture')}
                  title={t('sets.removePicture')}
                  onClick={() => act(() => api.removeContentSetImage(set.id, publicUrl))}
                >
                  ×
                </button>
              </div>
            ))}

            {/* Uploading is a file input wearing a button, so it cannot be one. */}
            <label className="set__add">
              {t('sets.add')}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (!file) return

                  act(() => api.uploadContentSetImage(set.id, file))
                }}
              />
            </label>
          </div>

          <div className="set__actions">
            {/* What the pictures are for. Two buttons rather than a choice to make first: a post,
                or the same post with the pictures cut into a video. */}
            <button
              type="button"
              className="button"
              disabled={busy || set.pictures.length === 0}
              onClick={() =>
                act(async () => {
                  await api.writePostFromSet(set.id, false)
                  onPostsWritten?.()
                })
              }
            >
              {t('sets.write')}
            </button>

            {set.pictures.length >= 2 && (
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() =>
                  act(async () => {
                    await api.writePostFromSet(set.id, true)
                    onPostsWritten?.()
                  })
                }
              >
                {t('sets.writeVideo')}
              </button>
            )}

            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => act(() => api.deleteContentSet(set.id))}
            >
              {t('sets.delete')}
            </button>
          </div>
        </div>
      ))}
    </section>
  )
}
